import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, Truck, Store, ArrowLeft, Tag, X, MapPin, Loader2, CreditCard, Banknote, ChevronDown } from 'lucide-react';
import { useCartStore } from '../lib/cartStore';
import { validateCoupon, applyCouponUsage, createOrder } from '../lib/api';
import { STORE_LAT, STORE_LNG, STORE_PHONE, FRETE_POR_KM, calcDistance } from '../lib/constants';
import { formatPrice } from '../lib/pricing';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import toast from 'react-hot-toast';

function getMaxParcelas(valor) {
  if (valor < 100) return 1;
  if (valor < 180) return 2;
  if (valor < 250) return 3;
  if (valor < 350) return 4;
  if (valor < 500) return 5;
  return 6;
}

export default function CartPage() {
  const items = useCartStore((s) => s.items);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const clearCart = useCartStore((s) => s.clearCart);
  const coupon = useCartStore((s) => s.coupon);
  const setCoupon = useCartStore((s) => s.setCoupon);

  const [tipoEntrega, setTipoEntrega] = useState('retirar');
  const [formaPagamento, setFormaPagamento] = useState('pix');
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState(1);
  const [endereco, setEndereco] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const [clientLat, setClientLat] = useState(null);
  const [clientLng, setClientLng] = useState(null);
  const [distanciaKm, setDistanciaKm] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Subtotal = preço cheio (riscado/cartão)
  const subtotal = items.reduce((sum, item) => sum + item.preco * item.quantidade, 0);
  const FRETE_GRATIS_MIN = 300;
  const DISTANCIA_MAX_KM = 50;
  const foraDeRegiao = distanciaKm !== null && distanciaKm > DISTANCIA_MAX_KM;
  const freteGratisPorSubtotal = subtotal >= FRETE_GRATIS_MIN;
  const freteGratis = coupon?.frete_gratis || freteGratisPorSubtotal;
  const valorFrete = tipoEntrega === 'retirar' ? 0 : freteGratis ? 0 : (distanciaKm !== null ? Math.round(distanciaKm * FRETE_POR_KM * 100) / 100 : 0);
  const faltaParaFreteGratis = Math.max(0, FRETE_GRATIS_MIN - subtotal);
  const discount = coupon?.tipo === 'porcentagem' ? subtotal * (coupon.valor / 100) : 0;

  // Subtotal após cupom
  const subtotalAposCupom = subtotal - discount;

  // Desconto PIX (10% = dividir por 1.1)
  const subtotalPix = Math.round((subtotalAposCupom / 1.1) * 100) / 100;
  const descontoPix = formaPagamento === 'pix' ? Math.round((subtotalAposCupom - subtotalPix) * 100) / 100 : 0;

  // Total final
  const totalProdutos = formaPagamento === 'pix' ? subtotalPix : subtotalAposCupom;
  const total = totalProdutos + valorFrete;

  // Parcelas disponíveis
  const maxParcelas = getMaxParcelas(subtotalAposCupom);
  const valorParcela = parcelasSelecionadas > 0 ? Math.round((subtotalAposCupom / parcelasSelecionadas) * 100) / 100 : subtotalAposCupom;

  const handleGetLocation = () => {
    if (!navigator.geolocation) { setGeoError('Geolocalização não suportada'); return; }
    setGeoLoading(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setClientLat(lat);
        setClientLng(lng);
        const dist = calcDistance(STORE_LAT, STORE_LNG, lat, lng);
        setDistanciaKm(Math.round(dist * 10) / 10);
        setGeoLoading(false);
        toast.success(`Localização obtida! Distância: ${dist.toFixed(1)} km`);
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError('Permissão de localização negada. Ative nas configurações do navegador.');
        else if (err.code === 2) setGeoError('Localização indisponível.');
        else setGeoError('Tempo esgotado. Tente novamente.');
        toast.error('Não foi possível obter a localização');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await validateCoupon(couponCode, subtotal);
      setCoupon(res.data);
      toast.success(res.data.message || 'Cupom aplicado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cupom inválido');
    }
    setCouponLoading(false);
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponCode('');
    toast.success('Cupom removido');
  };

  const handleFinalizarPedido = async () => {
    if (items.length === 0) { toast.error('Carrinho vazio'); return; }
    if (tipoEntrega === 'entregar' && !clientLat) { toast.error('Ative sua localização para entrega'); return; }
    if (tipoEntrega === 'entregar' && foraDeRegiao) { toast.error('Entrega indisponível para sua região. Fale conosco no WhatsApp!'); return; }

    let msg = `Olá MOTOVELOZ! 🏍️\nGostaria de fazer o seguinte pedido:\n\nPRODUTOS:\n`;
    items.forEach((item) => {
      const precoUnitarioExibido = formaPagamento === 'pix' ? Math.round((item.preco / 1.1) * 100) / 100 : item.preco;
      msg += `• ${item.quantidade} x - ${item.nome}\nCód: ${item.codigo}\nR$ ${formatPrice(precoUnitarioExibido * item.quantidade)}\n\n`;
    });

    // Forma de pagamento
    if (formaPagamento === 'pix') {
      msg += `💰 PAGAMENTO: PIX ou Dinheiro\n✅ Desconto de 10% aplicado!\n`;
    } else {
      if (parcelasSelecionadas > 1) {
        msg += `💳 PAGAMENTO: Cartão em ${parcelasSelecionadas}x de R$ ${formatPrice(valorParcela)} sem juros\n`;
      } else {
        msg += `💳 PAGAMENTO: Cartão à vista\n`;
      }
    }

    // Entrega
    if (tipoEntrega === 'retirar') {
      msg += `\n🏪 RETIRADA NA LOJA\n📍 Água Branca, Miguel Alves - PI\n`;
    } else {
      msg += `\n🚚 ENTREGA EM DOMICÍLIO\n`;
      if (clientLat && clientLng) {
        msg += `📍 Localização de Entrega: https://www.google.com/maps?q=${clientLat},${clientLng}\n`;
        msg += `Distância: ${distanciaKm?.toFixed(1) || '0.0'} km\n`;
      }
      if (endereco.trim()) {
        msg += `📫 Referência: ${endereco}\n`;
      }
      msg += `💰 Frete: ${freteGratis ? (freteGratisPorSubtotal ? 'GRÁTIS (compra acima de R$ 300)' : 'GRÁTIS (cupom)') : `R$ ${formatPrice(valorFrete)}`}\n`;
    }

    msg += `\n━━━━━━━━━━━━━━━━\n`;
    msg += `Subtotal: R$ ${formatPrice(subtotal)}\n`;

    if (coupon && discount > 0) {
      msg += `🎟️ Cupom: ${coupon.code} (-R$ ${formatPrice(discount)})\n`;
    }
    if (coupon?.frete_gratis) {
      msg += `🎟️ Cupom: ${coupon.code} (Frete Grátis)\n`;
    }
    if (formaPagamento === 'pix') {
      msg += `💰 Desconto PIX/Dinheiro (10%): -R$ ${formatPrice(descontoPix)}\n`;
    }
    if (tipoEntrega === 'entregar') {
      msg += `Frete: ${freteGratis ? 'Grátis' : `R$ ${formatPrice(valorFrete)}`}\n`;
    } else {
      msg += `Frete: Retirada grátis\n`;
    }
    msg += `TOTAL: R$ ${formatPrice(total)}\n`;
    msg += `━━━━━━━━━━━━━━━━\n\nAguardo confirmação do pedido!`;

    if (coupon) { try { await applyCouponUsage(coupon.code, subtotal); } catch(e) {} }

    try {
      await createOrder({
        items: items.map(i => ({ id: i.id, codigo: i.codigo, nome: i.nome, preco: i.preco, quantidade: i.quantidade })),
        subtotal, frete: valorFrete, discount: discount + descontoPix, total,
        tipo_entrega: tipoEntrega, endereco,
        client_lat: clientLat, client_lng: clientLng,
        distancia_km: distanciaKm,
        coupon_code: coupon?.code || null,
        forma_pagamento: formaPagamento,
        parcelas: formaPagamento === 'cartao' ? parcelasSelecionadas : null
      });
    } catch(e) {}

    window.open(`https://wa.me/${STORE_PHONE}?text=${encodeURIComponent(msg)}`, '_blank');
    clearCart();
    toast.success('Pedido enviado via WhatsApp!');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-[#09090B]">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16 pb-24 md:pb-16">
          <div className="text-center">
            <ShoppingBag className="h-20 w-20 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white uppercase tracking-tight mb-3" style={{ fontFamily: 'Teko, sans-serif' }}>Carrinho Vazio</h2>
            <p className="text-zinc-500 mb-8">Adicione produtos para continuar</p>
            <Link to="/produtos" data-testid="continue-shopping-btn" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 uppercase tracking-wider transition-all">
              <ArrowLeft className="h-5 w-5" /> Ver Produtos
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      <Header />
      <main className="flex-1 py-6 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="cart-title">Carrinho</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {items.map((item) => {
                const precoExibido = formaPagamento === 'pix' ? Math.round((item.preco / 1.1) * 100) / 100 : item.preco;
                return (
                  <div key={item.id} className="bg-zinc-900 border border-zinc-800 p-4 flex gap-4" data-testid={`cart-item-${item.id}`}>
                    <div className="w-20 h-20 bg-zinc-800 flex-shrink-0 overflow-hidden">
                      {item.imagemUrl ? <img src={item.imagemUrl} alt={item.nome} className="w-full h-full object-cover" /> : <ShoppingBag className="h-8 w-8 text-zinc-700 m-auto mt-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 translate="no" className="text-white font-semibold text-sm truncate">{item.nome}</h3>
                      <p className="text-zinc-600 text-xs">Cód: {item.codigo}</p>
                      {formaPagamento === 'pix' ? (
                        <div className="mt-1">
                          <span className="text-zinc-500 text-xs line-through mr-2">R$ {formatPrice(item.preco)}</span>
                          <span className="text-green-400 font-bold">R$ {formatPrice(precoExibido)}</span>
                        </div>
                      ) : (
                        <p className="text-red-500 font-bold mt-1">R$ {formatPrice(item.preco)}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-400 p-1" data-testid={`remove-item-${item.id}`}><Trash2 className="h-4 w-4" /></button>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(item.id, item.quantidade - 1)} className="bg-zinc-800 text-white p-1"><Minus className="h-3 w-3" /></button>
                        <span className="text-white font-bold text-sm w-6 text-center">{item.quantidade}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantidade + 1)} className="bg-zinc-800 text-white p-1"><Plus className="h-3 w-3" /></button>
                      </div>
                      <p className={`font-bold text-sm ${formaPagamento === 'pix' ? 'text-green-400' : 'text-white'}`}>
                        R$ {formatPrice(precoExibido * item.quantidade)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-zinc-900 border border-zinc-800 p-6 sticky top-24">
                <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Teko, sans-serif' }}>Resumo</h2>

                {/* Coupon */}
                <div className="mb-4">
                  <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">Cupom de desconto</label>
                  {coupon ? (
                    <div className="flex items-center justify-between bg-green-900/30 border border-green-600 p-3">
                      <div>
                        <p translate="no" className="text-green-400 font-bold text-sm">{coupon.code}</p>
                        <p className="text-green-400/70 text-xs">{coupon.frete_gratis ? 'Frete Grátis' : `-${coupon.valor}%`}</p>
                      </div>
                      <button onClick={handleRemoveCoupon} className="text-green-400 hover:text-red-400" data-testid="remove-coupon-btn"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="CÓDIGO"
                        className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-red-500" data-testid="coupon-input" />
                      <button onClick={handleApplyCoupon} disabled={couponLoading} data-testid="apply-coupon-btn"
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-bold text-sm uppercase disabled:opacity-50">
                        <Tag className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Forma de Pagamento */}
                <div className="mb-4">
                  <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">Forma de Pagamento</label>
                  <div className="space-y-2">
                    <button onClick={() => setFormaPagamento('pix')} data-testid="payment-pix"
                      className={`w-full p-3 border-2 transition-all flex items-center gap-3 ${formaPagamento === 'pix' ? 'border-green-500 bg-green-600/10' : 'border-zinc-700 bg-zinc-800'}`}>
                      <Banknote className={`h-5 w-5 ${formaPagamento === 'pix' ? 'text-green-500' : 'text-zinc-400'}`} />
                      <div className="text-left flex-1">
                        <p className="text-white font-bold text-sm">PIX ou Dinheiro</p>
                        <p className="text-green-400 text-xs font-semibold">10% de desconto</p>
                      </div>
                      {formaPagamento === 'pix' && <span className="text-green-400 text-xs font-bold">-10%</span>}
                    </button>
                    <button onClick={() => { setFormaPagamento('cartao'); setParcelasSelecionadas(maxParcelas > 1 ? maxParcelas : 1); }} data-testid="payment-card"
                      className={`w-full p-3 border-2 transition-all flex items-center gap-3 ${formaPagamento === 'cartao' ? 'border-blue-500 bg-blue-600/10' : 'border-zinc-700 bg-zinc-800'}`}>
                      <CreditCard className={`h-5 w-5 ${formaPagamento === 'cartao' ? 'text-blue-500' : 'text-zinc-400'}`} />
                      <div className="text-left flex-1">
                        <p className="text-white font-bold text-sm">Cartão de Crédito</p>
                        <p className="text-zinc-500 text-xs">Até {maxParcelas}x sem juros</p>
                      </div>
                    </button>
                  </div>

                  {/* PIX details */}
                  {formaPagamento === 'pix' && (
                    <div className="mt-2 bg-green-900/20 border border-green-700 p-3" data-testid="payment-pix-details">
                      <p className="text-green-400 text-xs font-bold">Desconto de 10% no Pix/Dinheiro aplicado!</p>
                      <p className="text-green-300 text-sm font-bold mt-1">Subtotal com desconto: R$ {formatPrice(subtotalPix)}</p>
                    </div>
                  )}

                  {/* Card: installment selector */}
                  {formaPagamento === 'cartao' && (
                    <div className="mt-2 bg-blue-900/20 border border-blue-700 p-3" data-testid="payment-card-details">
                      <p className="text-blue-400 text-xs font-bold mb-2">Escolha o parcelamento:</p>
                      <div className="relative">
                        <select
                          value={parcelasSelecionadas}
                          onChange={(e) => setParcelasSelecionadas(Number(e.target.value))}
                          data-testid="installment-select"
                          className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm p-2 pr-8 appearance-none focus:outline-none focus:border-blue-500 cursor-pointer"
                        >
                          <option value={1}>1x de R$ {formatPrice(subtotalAposCupom)} (à vista)</option>
                          {Array.from({ length: maxParcelas - 1 }, (_, i) => i + 2).map((n) => (
                            <option key={n} value={n}>
                              {n}x de R$ {formatPrice(Math.round((subtotalAposCupom / n) * 100) / 100)} sem juros
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
                      </div>
                      <p className="text-blue-300 text-sm font-bold mt-2">
                        Total: {parcelasSelecionadas > 1
                          ? `${parcelasSelecionadas}x de R$ ${formatPrice(valorParcela)} sem juros`
                          : `R$ ${formatPrice(subtotalAposCupom)} à vista`
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Delivery */}
                <div className="mb-4">
                  <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">Entrega</label>
                  <div className="space-y-2">
                    <button onClick={() => setTipoEntrega('retirar')} data-testid="delivery-pickup"
                      className={`w-full p-3 border-2 transition-all flex items-center gap-3 ${tipoEntrega === 'retirar' ? 'border-red-500 bg-red-600/10' : 'border-zinc-700 bg-zinc-800'}`}>
                      <Store className="h-5 w-5 text-red-500" />
                      <div className="text-left"><p className="text-white font-bold text-sm">Retirar na Loja</p><p className="text-zinc-500 text-xs">Sem frete</p></div>
                    </button>
                    <button onClick={() => setTipoEntrega('entregar')} data-testid="delivery-deliver"
                      className={`w-full p-3 border-2 transition-all flex items-center gap-3 ${tipoEntrega === 'entregar' ? 'border-red-500 bg-red-600/10' : 'border-zinc-700 bg-zinc-800'}`}>
                      <Truck className="h-5 w-5 text-red-500" />
                      <div className="text-left"><p className="text-white font-bold text-sm">Entrega em Domicílio</p><p className="text-zinc-500 text-xs">{freteGratis ? 'Grátis' : `R$ ${FRETE_POR_KM}/km`}</p></div>
                    </button>
                  </div>
                </div>

                {tipoEntrega === 'entregar' && (
                  <div className="mb-4 space-y-3">
                    <button onClick={handleGetLocation} disabled={geoLoading} data-testid="get-location-btn"
                      className={`w-full p-3 border-2 transition-all flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider ${
                        foraDeRegiao ? 'border-orange-500 bg-orange-600/10 text-orange-400' :
                        clientLat ? 'border-green-600 bg-green-600/10 text-green-400' : 'border-red-500 bg-red-600/10 text-white hover:bg-red-600/20'
                      }`}>
                      {geoLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <MapPin className="h-5 w-5" />}
                      {geoLoading ? 'Obtendo localização...' : clientLat ? `Localização obtida (${distanciaKm} km)` : 'Compartilhar Localização'}
                    </button>

                    {geoError && <p className="text-red-400 text-xs">{geoError}</p>}

                    {foraDeRegiao && (
                      <div className="bg-orange-900/20 border border-orange-600 p-4" data-testid="out-of-range-msg">
                        <p className="text-orange-400 font-bold text-sm mb-2">Poxa, ainda não chegamos na sua região (acima de 50km)!</p>
                        <p className="text-zinc-400 text-xs leading-relaxed">Mas estamos expandindo nossas entregas em breve. Enquanto isso, você pode <strong className="text-white">retirar na loja</strong> ou nos chamar no <strong className="text-[#25D366]">WhatsApp</strong> para combinarmos uma solução.</p>
                        <a href={`https://wa.me/${STORE_PHONE}?text=${encodeURIComponent('Olá MotoVeloz! Minha região fica a mais de 50km, podemos combinar uma entrega?')}`} target="_blank" rel="noopener noreferrer"
                          className="mt-3 w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white font-bold py-2.5 text-sm uppercase tracking-wider transition-all">
                          Falar no WhatsApp
                        </a>
                      </div>
                    )}

                    {clientLat && !foraDeRegiao && (
                      <div className="bg-zinc-800 p-3 border border-zinc-700">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-400">Distância:</span>
                          <span className="text-white font-bold">{distanciaKm} km</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-zinc-400">Valor do frete:</span>
                          <span className={`font-bold ${freteGratis ? 'text-green-400' : 'text-white'}`}>
                            {freteGratis ? (freteGratisPorSubtotal ? 'GRÁTIS (acima de R$300)' : 'GRÁTIS (cupom)') : `R$ ${valorFrete.toFixed(2)}`}
                          </span>
                        </div>
                        <p className="text-zinc-500 text-[10px] mt-1">R$ {FRETE_POR_KM}/km x {distanciaKm} km</p>
                      </div>
                    )}

                    <textarea value={endereco} onChange={(e) => setEndereco(e.target.value)} placeholder="Referência / Complemento (opcional)" rows={2}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-red-500 resize-none" data-testid="address-input" />

                    {freteGratisPorSubtotal && (
                      <p className="text-[#25D366] font-bold text-sm text-center" data-testid="free-shipping-msg">🎉 Parabéns! Você ganhou frete grátis!</p>
                    )}

                    {!freteGratis && (
                      <p className="text-[#DAA520] font-bold text-xs text-center" data-testid="frete-falta-msg">Faltam R$ {faltaParaFreteGratis.toFixed(2)} para frete grátis</p>
                    )}
                  </div>
                )}

                <div className="space-y-2 mb-4 pb-4 border-b border-zinc-800">
                  <div className="flex justify-between text-zinc-400 text-sm"><span>Subtotal</span><span className="font-bold">R$ {formatPrice(subtotal)}</span></div>
                  {discount > 0 && <div className="flex justify-between text-green-400 text-sm"><span>Cupom ({coupon?.valor}%)</span><span className="font-bold">-R$ {formatPrice(discount)}</span></div>}
                  {formaPagamento === 'pix' && (
                    <div className="flex justify-between text-green-400 text-sm"><span>Desconto PIX (10%)</span><span className="font-bold">-R$ {formatPrice(descontoPix)}</span></div>
                  )}
                  {tipoEntrega === 'entregar' && (
                    <div className="flex justify-between text-zinc-400 text-sm">
                      <span>Frete</span>
                      <span className={`font-bold ${freteGratis ? 'text-green-400' : ''}`}>
                        {freteGratis ? 'GRÁTIS' : clientLat ? `R$ ${formatPrice(valorFrete)}` : 'Aguardando localização'}
                      </span>
                    </div>
                  )}
                  {tipoEntrega === 'retirar' && <div className="flex justify-between text-zinc-400 text-sm"><span>Frete</span><span className="font-bold">Retirar na Loja</span></div>}
                </div>

                <div className="flex justify-between text-white text-2xl font-black mb-4" data-testid="cart-total">
                  <span>Total</span><span>R$ {formatPrice(total)}</span>
                </div>

                <button onClick={handleFinalizarPedido} data-testid="checkout-btn"
                  disabled={(tipoEntrega === 'entregar' && !clientLat) || foraDeRegiao}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5C] disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold py-4 uppercase tracking-wider text-lg transition-all flex items-center justify-center gap-2">
                  Finalizar via WhatsApp
                </button>
                {tipoEntrega === 'entregar' && !clientLat && (
                  <p className="text-yellow-500 text-xs text-center mt-2">Compartilhe sua localização para prosseguir</p>
                )}
                {foraDeRegiao && (
                  <p className="text-orange-400 text-xs text-center mt-2">Entrega fora da região. Retire na loja ou fale conosco no WhatsApp.</p>
                )}
                <Link to="/produtos" className="block text-center text-zinc-500 hover:text-white text-sm mt-3 transition-colors">Continuar comprando</Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
